import { useEffect, useMemo, useRef, useState } from "react";
import { Eye, EyeOff, Mail, MapPin } from "lucide-react";
import {
	CircleMarker,
	MapContainer,
	TileLayer,
	useMap,
	useMapEvents,
} from "react-leaflet";
import type { LatLngLiteral } from "leaflet";
import {
	composePhoneWithCountryCode,
	getPhoneCountryCodeOptions,
	splitPhoneInputValue,
	splitPhoneWithCountryCode,
} from "../../../core/utils/phone";
import type {
	PickedLocation,
	AddressSuggestion,
	StepPersonalProps,
} from "../../../core/types/tsx/components/wizard/provider-registration/StepPersonal.types";

function MapClickHandler({ onPick }: { onPick: (coords: PickedLocation) => void }) {
	useMapEvents({
		click(e) {
			onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
		},
	});
	return null;
}

function MapFlyTo({ center, zoom }: { center: LatLngLiteral; zoom: number }) {
	const map = useMap();
	useEffect(() => {
		map.flyTo(center, zoom, { duration: 1.2 });
	}, [map, center, zoom]);
	return null;
}

async function reverseGeocodeNominatim(
	coords: PickedLocation,
	signal?: AbortSignal,
): Promise<string | null> {
	const { lat, lng } = coords;
	const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
		lat,
	)}&lon=${encodeURIComponent(lng)}`;
	const res = await fetch(url, {
		signal,
		headers: {
			Accept: "application/json",
			"Accept-Language": "es",
		},
	});
	if (!res.ok) return null;
	const data = (await res.json()) as { display_name?: string };
	const displayName =
		typeof data.display_name === "string" ? data.display_name.trim() : "";
	return displayName || null;
}

async function searchAddressNominatim(
	query: string,
	signal?: AbortSignal,
): Promise<AddressSuggestion[]> {
	const q = query.trim();
	if (!q) return [];
	const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&q=${encodeURIComponent(q)}`;
	const res = await fetch(url, {
		signal,
		headers: {
			Accept: "application/json",
			"Accept-Language": "es",
		},
	});
	if (!res.ok) return [];
	const data = (await res.json()) as Array<{
		display_name?: string;
		lat?: string;
		lon?: string;
	}>;
	if (!Array.isArray(data)) return [];
	return data
		.map((item) => {
			const label =
				typeof item.display_name === "string" ? item.display_name.trim() : "";
			const lat = Number(item.lat);
			const lng = Number(item.lon);
			if (!label || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
			return { label, lat, lng };
		})
		.filter((x): x is AddressSuggestion => x !== null);
}

export function StepPersonal({
	personal,
	setPersonal,
	errors,
	clearError,
	showPassword,
	onToggleShowPassword,
	isAdminMode,
	inputBase,
	inputWithIcon,
	errorText,
}: StepPersonalProps) {
	const [pickedLocation, setPickedLocation] = useState<PickedLocation | null>(null);
	const [reverseGeocodeStatus, setReverseGeocodeStatus] = useState<
		"idle" | "loading" | "error"
	>("idle");
	const reverseGeocodeAbortRef = useRef<AbortController | null>(null);
	const [flyTarget, setFlyTarget] = useState<{ center: LatLngLiteral; zoom: number }>({
		center: { lat: 10.4806, lng: -66.9036 },
		zoom: 9,
	});
	const [geoStatus, setGeoStatus] = useState<"loading" | "done" | "error">("loading");
	const hasGeolocatedRef = useRef(false);
	const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
	const [addressSuggestStatus, setAddressSuggestStatus] = useState<
		"idle" | "loading" | "error"
	>("idle");
	const addressSuggestAbortRef = useRef<AbortController | null>(null);
	const addressSuggestDebounceRef = useRef<ReturnType<typeof window.setTimeout> | null>(
		null,
	);

	useEffect(() => {
		if (hasGeolocatedRef.current) return;
		hasGeolocatedRef.current = true;
		if (!navigator.geolocation) {
			setGeoStatus("error");
			return;
		}
		setGeoStatus("loading");
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				const lat = pos.coords.latitude;
				const lng = pos.coords.longitude;
				if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
					setGeoStatus("error");
					return;
				}
				setFlyTarget({ center: { lat, lng }, zoom: 16 });
				setGeoStatus("done");
			},
			(err) => {
				if (err && err.code === 1) {
					// PERMISSION_DENIED
					setGeoStatus("error");
				} else {
					setGeoStatus("loading");
				}
			},
			{ enableHighAccuracy: false },
		);
	}, []);

	useEffect(() => {
		return () => {
			reverseGeocodeAbortRef.current?.abort();
			addressSuggestAbortRef.current?.abort();
			if (addressSuggestDebounceRef.current)
				window.clearTimeout(addressSuggestDebounceRef.current);
		};
	}, []);

	const phoneParts = useMemo(
		() => splitPhoneWithCountryCode(personal.telefono),
		[personal.telefono],
	);

	const phoneCountryCodeOptions = useMemo(
		() => getPhoneCountryCodeOptions(phoneParts.countryCode),
		[phoneParts.countryCode],
	);

	return (
		<>
			<div className='grid gap-4 md:grid-cols-2'>
				<div className='space-y-2'>
					<label className='block text-xs font-medium text-slate-600'>
						Nombre *
					</label>
					<input
						type='text'
						value={personal.nombre}
						onChange={(e) => {
							clearError("nombre");
							setPersonal((p) => ({ ...p, nombre: e.target.value }));
						}}
						placeholder='Juan'
						autoComplete='given-name'
						className={inputBase}
					/>
					{errors.nombre ? <p className={errorText}>{errors.nombre}</p> : null}
				</div>
				<div className='space-y-2'>
					<label className='block text-xs font-medium text-slate-600'>
						Apellido *
					</label>
					<input
						type='text'
						value={personal.apellido}
						onChange={(e) => {
							clearError("apellido");
							setPersonal((p) => ({ ...p, apellido: e.target.value }));
						}}
						placeholder='Pérez'
						autoComplete='family-name'
						className={inputBase}
					/>
					{errors.apellido ? (
						<p className={errorText}>{errors.apellido}</p>
					) : null}
				</div>

				<div className='space-y-2'>
					<label
						htmlFor='register-provider-email'
						className='block text-xs font-medium text-slate-600'
					>
						Correo Electrónico *
					</label>
					<div className='relative'>
						<Mail className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
						<input
							id='register-provider-email'
							name='email'
							type='email'
							value={personal.email}
							onChange={(e) => {
								clearError("email");
								setPersonal((p) => ({ ...p, email: e.target.value }));
							}}
							placeholder='juan.perez@email.com'
							autoComplete='email'
							aria-label='Correo electrónico'
							required
							className={inputWithIcon}
						/>
					</div>
					{errors.email ? <p className={errorText}>{errors.email}</p> : null}
				</div>

				<div className='space-y-2'>
					<label className='block text-xs font-medium text-slate-600'>
						Teléfono *
					</label>
					<div className='grid grid-cols-[140px_1fr] gap-2 sm:grid-cols-[180px_1fr]'>
						<select
							value={phoneParts.countryCode}
							onChange={(e) => {
								clearError("telefono");
								setPersonal((p) => ({
									...p,
									telefono: composePhoneWithCountryCode(
										e.target.value,
										phoneParts.localNumber,
									),
								}));
							}}
							className={inputBase}
						>
							{phoneCountryCodeOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
						<input
							type='tel'
							inputMode='tel'
							pattern='^\+?[0-9()\s-]{7,20}$'
							value={phoneParts.localNumber}
							onChange={(e) => {
								const nextPhoneParts = splitPhoneInputValue(
									e.target.value,
									phoneParts.countryCode,
									{
										inferCallingCodeFromPlainDigits: false,
										codeOnlyMeansEmptyLocal: false,
									},
								);
								clearError("telefono");
								setPersonal((p) => ({
									...p,
									telefono: composePhoneWithCountryCode(
										nextPhoneParts.countryCode,
										nextPhoneParts.localNumber,
									),
								}));
							}}
							placeholder='412-1234567'
							autoComplete='tel'
							className={inputBase}
						/>
					</div>
					{errors.telefono ? (
						<p className={errorText}>{errors.telefono}</p>
					) : null}
				</div>


				{!isAdminMode ? (
					<div className='grid grid-cols-1 gap-4 sm:grid-cols-2 md:col-span-2'>
						<div className='space-y-2'>
							<label
								htmlFor='register-provider-password'
								className='block text-xs font-medium text-slate-600'
							>
								Contraseña *
							</label>
							<div className='relative'>
								<input
									id='register-provider-password'
									name='password'
									value={personal.password}
									onChange={(e) => {
										clearError("password");
										setPersonal((p) => ({
											...p,
											password: e.target.value,
										}));
									}}
									placeholder='••••••••'
									type={showPassword ? "text" : "password"}
									autoComplete='new-password'
									aria-label='Contraseña'
									required
									className={inputBase + " pr-10"}
								/>
								<button
									type='button'
									aria-label={
										showPassword
											? "Ocultar contraseña"
											: "Mostrar contraseña"
									}
									onClick={onToggleShowPassword}
									className='cursor-pointer absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50'
								>
									{showPassword ? (
										<EyeOff className='h-4 w-4' />
									) : (
										<Eye className='h-4 w-4' />
									)}
								</button>
							</div>
							{errors.password ? (
								<p className={errorText}>{errors.password}</p>
							) : null}
						</div>

						<div className='space-y-2'>
							<label
								htmlFor='register-provider-confirm-password'
								className='block text-xs font-medium text-slate-600'
							>
								Confirmar Contraseña *
							</label>
							<div className='relative'>
								<input
									id='register-provider-confirm-password'
									name='confirmPassword'
									value={personal.confirmPassword}
									onChange={(e) => {
										clearError("confirmPassword");
										setPersonal((p) => ({
											...p,
											confirmPassword: e.target.value,
										}));
									}}
									placeholder='••••••••'
									type={showPassword ? "text" : "password"}
									autoComplete='new-password'
									aria-label='Confirmar contraseña'
									required
									className={inputBase + " pr-10"}
								/>
								<button
									type='button'
									aria-label={
										showPassword
											? "Ocultar contraseña"
											: "Mostrar contraseña"
									}
									onClick={onToggleShowPassword}
									className='cursor-pointer absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50'
								>
									{showPassword ? (
										<EyeOff className='h-4 w-4' />
									) : (
										<Eye className='h-4 w-4' />
									)}
								</button>
							</div>
							{errors.confirmPassword ? (
								<p className={errorText}>{errors.confirmPassword}</p>
							) : null}
						</div>
					</div>
				) : null}

				<div className='space-y-2 md:col-span-2'>
					<label className='block text-xs font-medium text-slate-600'>
						Dirección *
					</label>
					<div className='relative'>
						<MapPin className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
						<input
							type='text'
							value={personal.direccion}
							onChange={(e) => {
								const nextValue = e.target.value;
								clearError("direccion");
								setPersonal((p) => ({ ...p, direccion: nextValue }));

								addressSuggestAbortRef.current?.abort();
								if (addressSuggestDebounceRef.current) {
									window.clearTimeout(
										addressSuggestDebounceRef.current,
									);
									addressSuggestDebounceRef.current = null;
								}
								setAddressSuggestStatus("idle");
								const q = nextValue.trim();
								if (q.length < 3) {
									setAddressSuggestions([]);
									return;
								}

								addressSuggestDebounceRef.current = window.setTimeout(
									async () => {
										setAddressSuggestStatus("loading");
										const controller = new AbortController();
										addressSuggestAbortRef.current = controller;
										try {
											const results = await searchAddressNominatim(
												q,
												controller.signal,
											);
											setAddressSuggestions(results);
											setAddressSuggestStatus("idle");
										} catch (err) {
											if (
												err instanceof DOMException &&
												err.name === "AbortError"
											)
												return;
											setAddressSuggestions([]);
											setAddressSuggestStatus("error");
										}
									},
									350,
								);
							}}
							onBlur={() => {
								window.setTimeout(() => setAddressSuggestions([]), 150);
							}}
							onKeyDown={(e) => {
								if (e.key === "Escape") setAddressSuggestions([]);
							}}
							placeholder='Ej: Caracas, Chacao'
							autoComplete='street-address'
							className={inputWithIcon}
						/>
						{addressSuggestions.length > 0 ? (
							<ul className='absolute left-0 right-0 top-full z-10 mt-1 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white'>
								{addressSuggestions.map((sug) => (
									<li
										key={`${sug.lat},${sug.lng},${sug.label}`}
										className='border-b border-slate-100 last:border-b-0'
									>
										<button
											type='button'
											className='cursor-pointer w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50'
											onMouseDown={(evt) => {
												evt.preventDefault();
												clearError("direccion");
												addressSuggestAbortRef.current?.abort();
												setPersonal((p) => ({
													...p,
													direccion: sug.label,
												}));
												setPickedLocation({
													lat: sug.lat,
													lng: sug.lng,
												});
												setFlyTarget({
													center: {
														lat: sug.lat,
														lng: sug.lng,
													},
													zoom: 16,
												});
												setAddressSuggestions([]);
												setAddressSuggestStatus("idle");
											}}
										>
											{sug.label}
										</button>
									</li>
								))}
							</ul>
						) : null}
					</div>
					{addressSuggestStatus === "loading" ? (
						<p className='mt-1 text-xs text-slate-500'>
							Buscando sugerencias...
						</p>
					) : null}
					{errors.direccion ? (
						<p className={errorText}>{errors.direccion}</p>
					) : null}

					<div className='mt-3 rounded-xl border border-slate-200 bg-white p-3'>
						<p className='text-xs text-slate-500'>
							Selecciona un punto en el mapa para autocompletar la
							dirección.
						</p>
						<div className='mt-3 h-64 overflow-hidden rounded-lg border border-slate-200'>
							{geoStatus === "loading" ? (
								<div className='mt-2 flex items-center gap-2 rounded-xl bg-slate-100 px-4 py-2 text-sm text-slate-500'>
									<span className='inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-400 border-t-transparent' />
									Obteniendo tu ubicación...
								</div>
							) : geoStatus === "error" ? (
								<div className='mt-2 rounded-xl bg-red-50 px-4 py-2 text-sm text-red-600'>
									No se pudo obtener tu ubicación. Puedes seleccionar
									manualmente en el mapa.
								</div>
							) : null}
							<MapContainer
								center={flyTarget.center}
								zoom={flyTarget.zoom}
								scrollWheelZoom
								className='h-full w-full'
							>
								<TileLayer
									attribution='&copy; OpenStreetMap contributors'
									url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
								/>
								<MapFlyTo
									center={flyTarget.center}
									zoom={flyTarget.zoom}
								/>
								<MapClickHandler
									onPick={async (coords) => {
										clearError("direccion");
										setPickedLocation(coords);
										setAddressSuggestions([]);
										setReverseGeocodeStatus("loading");
										reverseGeocodeAbortRef.current?.abort();
										const controller = new AbortController();
										reverseGeocodeAbortRef.current = controller;
										try {
											const address = await reverseGeocodeNominatim(
												coords,
												controller.signal,
											);
											setPersonal((p) => ({
												...p,
												direccion:
													address ??
													`${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
											}));
											setReverseGeocodeStatus(
												address ? "idle" : "error",
											);
											setFlyTarget({
												center: {
													lat: coords.lat,
													lng: coords.lng,
												},
												zoom: 16,
											});
										} catch (err) {
											if (
												err instanceof DOMException &&
												err.name === "AbortError"
											)
												return;
											setPersonal((p) => ({
												...p,
												direccion: `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
											}));
											setReverseGeocodeStatus("error");
											setFlyTarget({
												center: {
													lat: coords.lat,
													lng: coords.lng,
												},
												zoom: 16,
											});
										}
									}}
								/>
								{pickedLocation ? (
									<CircleMarker
										center={{
											lat: pickedLocation.lat,
											lng: pickedLocation.lng,
										}}
										radius={8}
									/>
								) : null}
							</MapContainer>
						</div>
						{reverseGeocodeStatus === "loading" ? (
							<p className='mt-2 text-xs text-slate-500'>
								Buscando dirección...
							</p>
						) : reverseGeocodeStatus === "error" ? (
							<p className='mt-2 text-xs text-slate-500'>
								No se pudo obtener una dirección exacta. Se guardaron
								coordenadas.
							</p>
						) : null}
						{pickedLocation ? (
							<p className='mt-1 text-xs text-slate-500'>
								Ubicación seleccionada: {pickedLocation.lat.toFixed(6)},{" "}
								{pickedLocation.lng.toFixed(6)}
							</p>
						) : null}
					</div>
				</div>
			</div>
		</>
	);
}
